import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Languages, Users, Globe, Lock, Search, Loader2, BarChart3, BookOpen, FileText,
  Eye, ArrowLeft, Shield, ShieldOff, Archive, Trash2, MoreHorizontal, Download,
  Mail, KeyRound, UserCheck, RotateCcw, UserPlus, AlertTriangle, Activity, Calendar,
  ArrowUpDown, ArrowUp, ArrowDown, X, Camera, Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import axios from 'axios';
import { API_BASE as API } from '../config/api';

// ─── Helpers ────────────────────────────────────────────────────────────────
const fmt = (d) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
const daysLeft = (deletedAt) => {
  if (!deletedAt) return '—';
  const ms = new Date(deletedAt).getTime() + 30 * 86400000 - Date.now();
  return ms > 0 ? `${Math.ceil(ms / 86400000)}d` : 'Expired';
};
const authType = (u) => {
  const hasPass = !!u.password_hash || u.auth_provider === 'local';
  const hasGoogle = !!u.google_linked || u.auth_provider === 'google';
  if (hasPass && hasGoogle) return 'Both';
  if (hasGoogle) return 'Google';
  return 'Email';
};
const avatarInitial = (name) => (name || '?')[0].toUpperCase();

// ─── Main Component ─────────────────────────────────────────────────────────
const AdminPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Data
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [langSearch, setLangSearch] = useState('');
  const [selectedLang, setSelectedLang] = useState(null);

  // User management state
  const [activeTab, setActiveTab] = useState('active');
  const [loginFilter, setLoginFilter] = useState('all');
  const [langFilter, setLangFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('not_admin');
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  // Dialogs
  const [archiveDialog, setArchiveDialog] = useState(null); // {user, bulk?}
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [permDeleteDialog, setPermDeleteDialog] = useState(null);
  const [messageDialog, setMessageDialog] = useState(null); // {users:[]}
  const [inviteDialog, setInviteDialog] = useState(false);
  const [viewLangsDialog, setViewLangsDialog] = useState(null);
  const [msgSubject, setMsgSubject] = useState('');
  const [msgBody, setMsgBody] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [dialogLoading, setDialogLoading] = useState(false);

  // Selected user detail
  const [selectedUser, setSelectedUser] = useState(null);

  // ── Guards ──
  useEffect(() => {
    if (user && !user.is_admin) { toast.error('Admin access required'); navigate('/dashboard'); }
  }, [user, navigate]);

  // ── Data fetching ──
  const fetchStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(r.data);
    } catch (e) {
      if (e.response?.status === 403) { toast.error('Admin access required'); navigate('/dashboard'); }
    }
  }, [navigate]);

  const fetchUsers = useCallback(async (status) => {
    try {
      const r = await axios.get(`${API}/admin/users`, { params: { status, limit: 500 }, withCredentials: true });
      setUsers(r.data.users);
    } catch {}
  }, []);

  const fetchLanguages = useCallback(async (search = '') => {
    try {
      const r = await axios.get(`${API}/admin/languages`, { params: { search, limit: 100 }, withCredentials: true });
      setLanguages(r.data.languages);
    } catch {}
  }, []);

  const fetchLanguageDetail = async (conlangId) => {
    try {
      const r = await axios.get(`${API}/admin/languages/${conlangId}`, { withCredentials: true });
      setSelectedLang(r.data);
    } catch { toast.error('Failed to load language details'); }
  };

  useEffect(() => {
    Promise.all([fetchStats(), fetchUsers('active'), fetchLanguages()]).finally(() => setLoading(false));
  }, [fetchStats, fetchUsers, fetchLanguages]);

  // Re-fetch users when tab changes
  useEffect(() => {
    fetchUsers(activeTab);
    // Reset filters on tab change
    setLoginFilter('all');
    setLangFilter('all');
    setRoleFilter(activeTab === 'active' ? 'not_admin' : 'all');
  }, [activeTab, fetchUsers]);

  const refreshAll = () => { fetchStats(); fetchUsers(activeTab); };

  // ── Filtering (client-side) ──
  const filteredUsers = useMemo(() => {
    let list = [...users];
    // Login filter
    if (loginFilter === 'never') list = list.filter(u => !u.last_login);
    else if (loginFilter === '7d') list = list.filter(u => u.last_login && Date.now() - new Date(u.last_login).getTime() > 7 * 86400000);
    else if (loginFilter === '30d') list = list.filter(u => u.last_login && Date.now() - new Date(u.last_login).getTime() > 30 * 86400000);
    else if (loginFilter === '90d') list = list.filter(u => u.last_login && Date.now() - new Date(u.last_login).getTime() > 90 * 86400000);
    // Languages filter
    if (langFilter === 'none') list = list.filter(u => (u.language_count || 0) === 0);
    else if (langFilter === 'has') list = list.filter(u => (u.language_count || 0) > 0);
    // Role filter (active tab only)
    if (activeTab === 'active') {
      if (roleFilter === 'not_admin') list = list.filter(u => !u.is_admin);
      else if (roleFilter === 'admin') list = list.filter(u => u.is_admin);
    }
    // Sort
    list.sort((a, b) => {
      let va = a[sortKey], vb = b[sortKey];
      if (sortKey === 'language_count') { va = va || 0; vb = vb || 0; }
      if (typeof va === 'string') { va = va.toLowerCase(); vb = (vb || '').toLowerCase(); }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [users, loginFilter, langFilter, roleFilter, activeTab, sortKey, sortDir]);

  const hasActiveFilters = loginFilter !== 'all' || langFilter !== 'all' || (activeTab === 'active' && roleFilter !== 'not_admin');
  const bulkEligible = filteredUsers.filter(u => !u.is_admin && u.user_id !== user?.user_id);

  const clearFilters = () => { setLoginFilter('all'); setLangFilter('all'); setRoleFilter(activeTab === 'active' ? 'not_admin' : 'all'); };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown className="w-3.5 h-3.5 ml-1 opacity-40" />;
    return sortDir === 'asc' ? <ArrowUp className="w-3.5 h-3.5 ml-1 text-primary" /> : <ArrowDown className="w-3.5 h-3.5 ml-1 text-primary" />;
  };

  // ── Actions ──
  const doArchive = async (userId, notify) => {
    try {
      const r = await axios.post(`${API}/admin/users/${userId}/archive`, { notify }, { withCredentials: true });
      toast.success(r.data.message); refreshAll(); setArchiveDialog(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const doDelete = async (userId) => {
    try {
      const r = await axios.post(`${API}/admin/users/${userId}/delete`, {}, { withCredentials: true });
      toast.success(r.data.message); refreshAll(); setDeleteDialog(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const doRestore = async (userId) => {
    try {
      const r = await axios.post(`${API}/admin/users/${userId}/restore`, {}, { withCredentials: true });
      toast.success(r.data.message); refreshAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const doPermDelete = async (userId) => {
    try {
      const r = await axios.post(`${API}/admin/users/${userId}/permanent-delete`, {}, { withCredentials: true });
      toast.success(r.data.message); refreshAll(); setPermDeleteDialog(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const doRoleChange = async (userId, role) => {
    try {
      const r = await axios.patch(`${API}/admin/users/${userId}/role`, { role }, { withCredentials: true });
      toast.success(r.data.message); refreshAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const doResetPassword = async (userId) => {
    try {
      const r = await axios.post(`${API}/admin/users/${userId}/admin-reset-password`, {}, { withCredentials: true });
      toast.success(r.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const doExport = async (userId) => {
    try {
      const r = await axios.get(`${API}/admin/users/${userId}/export`, { withCredentials: true });
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `user_${userId}_export.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (e) { toast.error('Export failed'); }
  };
  const doImpersonate = async (userId) => {
    try {
      const r = await axios.post(`${API}/admin/users/${userId}/impersonate`, {}, { withCredentials: true });
      localStorage.setItem('impersonating', JSON.stringify({ name: r.data.user_name, email: r.data.user_email }));
      toast.success(`Now viewing as ${r.data.user_name}`);
      window.location.href = '/dashboard';
    } catch (e) { toast.error(e.response?.data?.detail || 'Impersonation failed'); }
  };
  const doSendMessage = async () => {
    if (!msgSubject.trim() || !msgBody.trim()) { toast.error('Subject and message required'); return; }
    setDialogLoading(true);
    try {
      const targets = messageDialog.users;
      if (targets.length === 1) {
        await axios.post(`${API}/admin/users/${targets[0].user_id}/send-message`, { subject: msgSubject, body: msgBody }, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/bulk/send-message`, { user_ids: targets.map(u => u.user_id), subject: msgSubject, body: msgBody }, { withCredentials: true });
      }
      toast.success(`Email sent to ${targets.length} user(s)`);
      setMessageDialog(null); setMsgSubject(''); setMsgBody('');
    } catch (e) { toast.error('Failed to send'); }
    setDialogLoading(false);
  };
  const doInvite = async () => {
    if (!inviteEmail.trim()) return;
    setDialogLoading(true);
    try {
      const r = await axios.post(`${API}/admin/invite`, { email: inviteEmail }, { withCredentials: true });
      toast.success(r.data.message);
      setInviteDialog(false); setInviteEmail('');
      if (r.data.action === 'promoted') refreshAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setDialogLoading(false);
  };
  const doViewLangs = async (u) => {
    try {
      const r = await axios.get(`${API}/admin/users/${u.user_id}/languages`, { withCredentials: true });
      setViewLangsDialog({ user: u, languages: r.data });
    } catch { toast.error('Failed to load'); }
  };

  // Bulk actions
  const doBulkArchive = async (notify) => {
    setDialogLoading(true);
    try {
      const r = await axios.post(`${API}/admin/bulk/archive`, { user_ids: bulkEligible.map(u => u.user_id), notify }, { withCredentials: true });
      toast.success(r.data.message); refreshAll(); setArchiveDialog(null);
    } catch (e) { toast.error('Failed'); }
    setDialogLoading(false);
  };
  const doBulkDelete = async () => {
    setDialogLoading(true);
    try {
      const r = await axios.post(`${API}/admin/bulk/delete`, { user_ids: bulkEligible.map(u => u.user_id) }, { withCredentials: true });
      toast.success(r.data.message); refreshAll(); setDeleteDialog(null);
    } catch (e) { toast.error('Failed'); }
    setDialogLoading(false);
  };
  const doBulkRestore = async () => {
    setDialogLoading(true);
    try {
      const r = await axios.post(`${API}/admin/bulk/restore`, { user_ids: filteredUsers.map(u => u.user_id) }, { withCredentials: true });
      toast.success(r.data.message); refreshAll();
    } catch (e) { toast.error('Failed'); }
    setDialogLoading(false);
  };
  const doBulkPermDelete = async () => {
    setDialogLoading(true);
    try {
      const r = await axios.post(`${API}/admin/bulk/permanent-delete`, { user_ids: bulkEligible.map(u => u.user_id) }, { withCredentials: true });
      toast.success(r.data.message); refreshAll(); setPermDeleteDialog(null);
    } catch (e) { toast.error('Failed'); }
    setDialogLoading(false);
  };

  // ── Language search ──
  const handleLangSearch = (e) => { e.preventDefault(); fetchLanguages(langSearch); };

  if (loading) {
    return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="w-12 h-12 text-primary animate-spin" /></div>;
  }

  // ─── RENDER ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background noise-overlay" data-testid="admin-page">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-foreground-muted hover:text-foreground" data-testid="admin-back-link">
            <ArrowLeft className="w-4 h-4" /><span>Back to Dashboard</span>
          </Link>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            <span className="font-heading text-lg font-semibold text-foreground">Admin Panel</span>
          </div>
        </div>
      </header>

      {/* Admin warning banner */}
      {stats && stats.admin_count < 2 && (
        <div className="bg-amber-500/10 border-b border-amber-500/30 px-6 py-3" data-testid="admin-warning-banner">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2 text-amber-400">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm font-medium">Only {stats.admin_count} admin{stats.admin_count !== 1 ? 's' : ''} configured. Add at least one more for safety.</span>
            </div>
            <Button size="sm" variant="outline" className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10" onClick={() => setInviteDialog(true)}>
              <UserPlus className="w-3.5 h-3.5 mr-1" /> Add Admin
            </Button>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-background-paper border border-border">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
            <TabsTrigger value="languages" data-testid="tab-languages">Languages</TabsTrigger>
            <TabsTrigger value="bugs" data-testid="tab-bugs">Bug Reports</TabsTrigger>
          </TabsList>

          {/* ── OVERVIEW TAB ── */}
          <TabsContent value="overview" className="space-y-6">
            <h2 className="font-heading text-2xl font-bold text-foreground">Platform Statistics</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={Users} label="Total Users" value={stats?.total_users || 0} sub={`+${stats?.users?.new_this_week || 0} this week`} />
              <StatCard icon={Languages} label="Total Languages" value={stats?.total_languages || 0} sub={`${stats?.languages?.public || 0} public`} />
              <StatCard icon={Activity} label="Active (7d)" value={stats?.active_7d || 0} color="text-green-400" />
              <StatCard icon={Calendar} label="Active (30d)" value={stats?.active_30d || 0} color="text-blue-400" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="bg-background-paper border-border">
                <CardHeader><CardTitle className="text-lg text-foreground">User Authentication</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between"><span className="text-foreground-muted">Email/Password</span><span className="text-foreground font-medium">{stats?.users?.email_auth || 0}</span></div>
                  <div className="flex justify-between"><span className="text-foreground-muted">Google OAuth</span><span className="text-foreground font-medium">{stats?.users?.google_auth || 0}</span></div>
                  <div className="flex justify-between pt-2 border-t border-border"><span className="text-foreground-muted">New this month</span><span className="text-primary font-medium">+{stats?.users?.new_this_month || 0}</span></div>
                </CardContent>
              </Card>
              <Card className="bg-background-paper border-border">
                <CardHeader><CardTitle className="text-lg text-foreground">Top Creators</CardTitle></CardHeader>
                <CardContent>
                  {stats?.top_creators?.length > 0 ? (
                    <div className="space-y-2">
                      {stats.top_creators.map((c, i) => (
                        <div key={c._id} className="flex justify-between"><span className="text-foreground-muted truncate">{c.name || c.email}</span><span className="text-foreground font-medium">{c.count} languages</span></div>
                      ))}
                    </div>
                  ) : <p className="text-foreground-subtle">No data yet</p>}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ── USERS TAB ── */}
          <TabsContent value="users" className="space-y-4" data-testid="users-tab-content">
            {/* Sub-tabs */}
            <div className="flex items-center gap-3 flex-wrap">
              {['active', 'archived', 'deleted'].map(t => (
                <Button
                  key={t}
                  variant={activeTab === t ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setActiveTab(t)}
                  data-testid={`user-tab-${t}`}
                >
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                  <span className="ml-1.5 text-xs opacity-70">
                    ({t === 'active' ? (stats?.total_users || 0) : t === 'archived' ? (stats?.archived_users || 0) : (stats?.deleted_users || 0)})
                  </span>
                </Button>
              ))}
              <div className="ml-auto">
                <Button size="sm" variant="outline" onClick={() => setInviteDialog(true)} data-testid="invite-admin-btn">
                  <UserPlus className="w-3.5 h-3.5 mr-1" /> Add Admin
                </Button>
              </div>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3 flex-wrap" data-testid="filters-bar">
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-foreground-muted whitespace-nowrap">Last Login</label>
                <Select value={loginFilter} onValueChange={setLoginFilter}>
                  <SelectTrigger className="w-[150px] h-8 text-xs bg-background-paper border-border" data-testid="filter-login"><SelectValue placeholder="Last Login" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="never">Never logged in</SelectItem>
                    <SelectItem value="7d">&gt; 7 days ago</SelectItem>
                    <SelectItem value="30d">&gt; 30 days ago</SelectItem>
                    <SelectItem value="90d">&gt; 90 days ago</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-foreground-muted whitespace-nowrap">Languages</label>
                <Select value={langFilter} onValueChange={setLangFilter}>
                  <SelectTrigger className="w-[140px] h-8 text-xs bg-background-paper border-border" data-testid="filter-languages"><SelectValue placeholder="Languages" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="none">No languages</SelectItem>
                    <SelectItem value="has">Has languages</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {activeTab === 'active' && (
                <div className="flex items-center gap-1.5">
                  <label className="text-xs text-foreground-muted whitespace-nowrap">Role</label>
                  <Select value={roleFilter} onValueChange={setRoleFilter}>
                    <SelectTrigger className="w-[130px] h-8 text-xs bg-background-paper border-border" data-testid="filter-role"><SelectValue placeholder="Role" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_admin">Not Admin</SelectItem>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" className="h-8 text-xs text-foreground-muted" onClick={clearFilters} data-testid="clear-filters">
                  <X className="w-3 h-3 mr-1" /> Clear
                </Button>
              )}
              {hasActiveFilters && (
                <span className="text-xs text-foreground-muted ml-auto" data-testid="filter-count">
                  Showing {filteredUsers.length} of {users.length} users
                </span>
              )}
            </div>

            {/* Bulk Actions */}
            {(activeTab !== 'active' || hasActiveFilters) && filteredUsers.length > 0 && (
              <div className="flex items-center gap-3 flex-wrap bg-background-paper border border-border rounded-lg px-4 py-2" data-testid="bulk-actions-bar">
                <span className="text-sm text-foreground-muted font-medium">{bulkEligible.length} user{bulkEligible.length !== 1 ? 's' : ''} eligible</span>
                <div className="ml-auto flex gap-2">
                  {activeTab === 'active' && roleFilter === 'not_admin' && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => setArchiveDialog({ bulk: true })} data-testid="bulk-archive-btn"><Archive className="w-3.5 h-3.5 mr-1" /> Archive All</Button>
                      <Button size="sm" variant="outline" className="text-destructive border-destructive/30" onClick={() => setDeleteDialog({ bulk: true })} data-testid="bulk-delete-btn"><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete All</Button>
                    </>
                  )}
                  {activeTab === 'archived' && (
                    <>
                      <Button size="sm" variant="outline" onClick={doBulkRestore} data-testid="bulk-restore-btn"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Restore All</Button>
                      <Button size="sm" variant="outline" className="text-destructive border-destructive/30" onClick={() => setDeleteDialog({ bulk: true })} data-testid="bulk-delete-archived-btn"><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete All</Button>
                    </>
                  )}
                  {activeTab === 'deleted' && (
                    <>
                      <Button size="sm" variant="outline" onClick={doBulkRestore} data-testid="bulk-restore-deleted-btn"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Restore All</Button>
                      <Button size="sm" variant="outline" className="text-destructive border-destructive/30" onClick={() => setPermDeleteDialog({ bulk: true })} data-testid="bulk-perm-delete-btn"><Trash2 className="w-3.5 h-3.5 mr-1" /> Permanently Delete All</Button>
                    </>
                  )}
                  <Button size="sm" variant="outline" onClick={() => setMessageDialog({ users: filteredUsers })} data-testid="bulk-email-btn">
                    <Mail className="w-3.5 h-3.5 mr-1" /> Email All ({filteredUsers.length})
                  </Button>
                </div>
              </div>
            )}

            {/* User Table */}
            <Card className="bg-background-paper border-border">
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full" data-testid="users-table">
                  <thead>
                    <tr className="border-b border-border">
                      <Th col="name" label="Name" sortKey={sortKey} sortDir={sortDir} toggle={toggleSort} />
                      <Th col="email" label="Email" sortKey={sortKey} sortDir={sortDir} toggle={toggleSort} />
                      <th className="text-left p-3 text-foreground-muted font-medium text-sm">Auth</th>
                      <Th col="language_count" label="Languages" sortKey={sortKey} sortDir={sortDir} toggle={toggleSort} center />
                      <Th col="created_at" label="Created" sortKey={sortKey} sortDir={sortDir} toggle={toggleSort} />
                      <Th col="last_login" label="Last Login" sortKey={sortKey} sortDir={sortDir} toggle={toggleSort} />
                      {activeTab === 'deleted' && <th className="text-left p-3 text-foreground-muted font-medium text-sm">Days Left</th>}
                      <th className="p-3 w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.length === 0 && (
                      <tr><td colSpan={activeTab === 'deleted' ? 8 : 7} className="text-center py-12 text-foreground-muted">No users found</td></tr>
                    )}
                    {filteredUsers.map(u => (
                      <tr key={u.user_id} className="border-b border-border/50 hover:bg-accent/30 transition-colors" data-testid={`user-row-${u.user_id}`}>
                        <td className="p-3">
                          <div className="flex items-center gap-2.5">
                            {u.picture ? (
                              <img src={u.picture} alt="" className="w-7 h-7 rounded-full" />
                            ) : (
                              <div className="w-7 h-7 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold">{avatarInitial(u.name)}</div>
                            )}
                            <span className="text-foreground font-medium text-sm">{u.name}</span>
                            {u.is_admin && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-primary/15 text-primary"><Shield className="w-2.5 h-2.5" /> Admin</span>}
                          </div>
                        </td>
                        <td className="p-3 text-foreground-muted text-sm">{u.email}</td>
                        <td className="p-3 text-foreground-muted text-sm">{authType(u)}</td>
                        <td className="p-3 text-foreground text-sm text-center">{u.language_count || 0}</td>
                        <td className="p-3 text-foreground-muted text-sm">{fmt(u.created_at)}</td>
                        <td className="p-3 text-foreground-muted text-sm">{fmt(u.last_login)}</td>
                        {activeTab === 'deleted' && <td className="p-3 text-amber-400 text-sm font-medium">{daysLeft(u.deleted_at)}</td>}
                        <td className="p-3">
                          <UserDropdown
                            u={u}
                            tab={activeTab}
                            isSelf={u.user_id === user?.user_id}
                            onViewLangs={() => doViewLangs(u)}
                            onExport={() => doExport(u.user_id)}
                            onImpersonate={() => doImpersonate(u.user_id)}
                            onMessage={() => { setMessageDialog({ users: [u] }); }}
                            onResetPw={() => doResetPassword(u.user_id)}
                            onRoleChange={() => doRoleChange(u.user_id, u.is_admin ? 'user' : 'admin')}
                            onArchive={() => setArchiveDialog({ user: u })}
                            onDelete={() => setDeleteDialog({ user: u })}
                            onRestore={() => doRestore(u.user_id)}
                            onPermDelete={() => setPermDeleteDialog({ user: u })}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── LANGUAGES TAB ── */}
          <TabsContent value="languages" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-2xl font-bold text-foreground">Languages</h2>
              <form onSubmit={handleLangSearch} className="flex gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground-subtle" />
                  <Input placeholder="Search languages..." value={langSearch} onChange={(e) => setLangSearch(e.target.value)} className="pl-10 bg-background-paper border-border w-64" />
                </div>
                <Button type="submit" variant="outline">Search</Button>
              </form>
            </div>
            {selectedLang ? (
              <LangDetail lang={selectedLang} onBack={() => setSelectedLang(null)} />
            ) : (
              <Card className="bg-background-paper border-border">
                <CardContent className="p-0">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left p-4 text-foreground-muted font-medium">Name</th>
                        <th className="text-left p-4 text-foreground-muted font-medium">Owner</th>
                        <th className="text-left p-4 text-foreground-muted font-medium">Visibility</th>
                        <th className="text-left p-4 text-foreground-muted font-medium">Words</th>
                        <th className="text-left p-4 text-foreground-muted font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {languages.map(lang => (
                        <tr key={lang.conlang_id} className="border-b border-border hover:bg-accent/50">
                          <td className="p-4">
                            <p className="text-foreground font-medium">{lang.name}</p>
                            {lang.native_name && <p className="text-secondary text-sm font-conlang">{lang.native_name}</p>}
                          </td>
                          <td className="p-4 text-foreground-muted">{lang.owner_name}</td>
                          <td className="p-4">
                            {lang.visibility === 'public'
                              ? <span className="flex items-center gap-1 text-green-500"><Globe className="w-4 h-4" /> Public</span>
                              : <span className="flex items-center gap-1 text-foreground-subtle"><Lock className="w-4 h-4" /> Private</span>}
                          </td>
                          <td className="p-4 text-foreground">{lang.word_count}</td>
                          <td className="p-4">
                            <Button variant="ghost" size="sm" onClick={() => fetchLanguageDetail(lang.conlang_id)}><Eye className="w-4 h-4 mr-1" /> View</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ── BUG REPORTS TAB ── */}
          <TabsContent value="bugs" className="space-y-6">
            <BugReportsPanel />
          </TabsContent>
        </Tabs>
      </main>

      {/* ── DIALOGS ── */}
      {/* Archive Dialog */}
      <Dialog open={!!archiveDialog} onOpenChange={() => setArchiveDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Archive className="w-5 h-5" /> {archiveDialog?.bulk ? 'Archive Users' : 'Archive User'}</DialogTitle>
            <DialogDescription>
              {archiveDialog?.bulk
                ? <>{bulkEligible.length} user(s) will be archived. They can reactivate by logging in within 30 days. <em className="text-foreground-subtle">Admin accounts are excluded.</em></>
                : <>{archiveDialog?.user?.name} ({archiveDialog?.user?.email})? They can reactivate by logging in within 30 days. Would you like to notify them by email?</>}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setArchiveDialog(null)}>Cancel</Button>
            <Button variant="outline" onClick={() => archiveDialog?.bulk ? doBulkArchive(false) : doArchive(archiveDialog?.user?.user_id, false)} disabled={dialogLoading}>Archive Silently</Button>
            <Button onClick={() => archiveDialog?.bulk ? doBulkArchive(true) : doArchive(archiveDialog?.user?.user_id, true)} disabled={dialogLoading}>
              {dialogLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Archive & Notify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive"><AlertTriangle className="w-5 h-5" /> {deleteDialog?.bulk ? 'Delete Users' : 'Delete User'}</DialogTitle>
            <DialogDescription>
              {deleteDialog?.bulk
                ? <>{bulkEligible.length} user(s) will be moved to deleted for 30 days before permanent deletion.</>
                : <>Their account will be moved to deleted for 30 days before permanent deletion.</>}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => deleteDialog?.bulk ? doBulkDelete() : doDelete(deleteDialog?.user?.user_id)} disabled={dialogLoading}>
              {dialogLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Delete {deleteDialog?.bulk ? 'Users' : 'User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permanent Delete Dialog */}
      <Dialog open={!!permDeleteDialog} onOpenChange={() => setPermDeleteDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive"><AlertTriangle className="w-5 h-5" /> Permanently Delete {permDeleteDialog?.bulk ? 'Users' : 'User'}</DialogTitle>
            <DialogDescription>
              {permDeleteDialog?.bulk
                ? <>This will permanently delete {bulkEligible.length} user(s) and all their data. This cannot be undone.</>
                : <>This will permanently delete <strong>{permDeleteDialog?.user?.name}</strong> and all their data. This cannot be undone.</>}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermDeleteDialog(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => permDeleteDialog?.bulk ? doBulkPermDelete() : doPermDelete(permDeleteDialog?.user?.user_id)} disabled={dialogLoading}>
              {dialogLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Permanently Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Message Dialog */}
      <Dialog open={!!messageDialog} onOpenChange={() => { setMessageDialog(null); setMsgSubject(''); setMsgBody(''); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5" /> Send Email</DialogTitle>
            <DialogDescription>
              {messageDialog?.users?.length === 1
                ? <>Send an email to {messageDialog.users[0].name} ({messageDialog.users[0].email})</>
                : <>Send an email to {messageDialog?.users?.length} recipients</>}
            </DialogDescription>
          </DialogHeader>
          {messageDialog?.users?.length > 1 && (
            <ScrollArea className="max-h-24 border border-border rounded p-2 text-xs text-foreground-muted">
              {messageDialog.users.map(u => <div key={u.user_id}>{u.email}</div>)}
            </ScrollArea>
          )}
          <div className="space-y-3">
            <div><Label>Subject</Label><Input value={msgSubject} onChange={e => setMsgSubject(e.target.value)} placeholder="Email subject" data-testid="msg-subject" /></div>
            <div><Label>Message</Label><Textarea value={msgBody} onChange={e => setMsgBody(e.target.value)} placeholder="Write your message..." className="min-h-[120px]" data-testid="msg-body" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setMessageDialog(null); setMsgSubject(''); setMsgBody(''); }}>Cancel</Button>
            <Button onClick={doSendMessage} disabled={dialogLoading}>
              {dialogLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {messageDialog?.users?.length === 1 ? 'Send Email' : `Send to ${messageDialog?.users?.length}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Admin Dialog */}
      <Dialog open={inviteDialog} onOpenChange={setInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5" /> Add Admin</DialogTitle>
            <DialogDescription>Enter an email address. If the person already has an account, they'll be promoted to admin immediately. If not, they'll receive an invitation to register as an admin.</DialogDescription>
          </DialogHeader>
          <div><Label>Email</Label><Input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="admin@example.com" data-testid="invite-email" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setInviteDialog(false); setInviteEmail(''); }}>Cancel</Button>
            <Button onClick={doInvite} disabled={dialogLoading || !inviteEmail.trim()}>
              {dialogLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Send Invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Languages Dialog */}
      <Dialog open={!!viewLangsDialog} onOpenChange={() => setViewLangsDialog(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{viewLangsDialog?.user?.name}'s Languages</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            {viewLangsDialog?.languages?.length === 0 && <p className="text-foreground-muted text-sm py-4 text-center">No languages</p>}
            {viewLangsDialog?.languages?.map(lang => (
              <div key={lang.conlang_id} className="flex items-center justify-between p-3 border-b border-border/50">
                <div>
                  <Link to={`/conlang/${lang.conlang_id}`} className="text-foreground font-medium text-sm hover:text-primary">{lang.name}</Link>
                  <p className="text-foreground-subtle text-xs">{lang.word_count || 0} words</p>
                </div>
                <div className="flex items-center gap-2">
                  {lang.visibility === 'public' ? <Globe className="w-3.5 h-3.5 text-green-500" /> : <Lock className="w-3.5 h-3.5 text-foreground-subtle" />}
                </div>
              </div>
            ))}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ─── Sub-components ─────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <Card className="bg-background-paper border-border">
    <CardHeader className="pb-2">
      <CardTitle className="text-sm text-foreground-muted flex items-center gap-2">
        <Icon className={`w-4 h-4 ${color || ''}`} />{label}
      </CardTitle>
    </CardHeader>
    <CardContent>
      <p className="text-3xl font-bold text-foreground">{value}</p>
      {sub && <p className="text-xs text-foreground-subtle mt-1">{sub}</p>}
    </CardContent>
  </Card>
);

const Th = ({ col, label, sortKey, sortDir, toggle, center }) => (
  <th className={`p-3 text-foreground-muted font-medium text-sm cursor-pointer select-none hover:text-foreground ${center ? 'text-center' : 'text-left'}`} onClick={() => toggle(col)}>
    <span className="inline-flex items-center">
      {label}
      {sortKey === col
        ? (sortDir === 'asc' ? <ArrowUp className="w-3.5 h-3.5 ml-1 text-primary" /> : <ArrowDown className="w-3.5 h-3.5 ml-1 text-primary" />)
        : <ArrowUpDown className="w-3.5 h-3.5 ml-1 opacity-40" />}
    </span>
  </th>
);

const UserDropdown = ({ u, tab, isSelf, onViewLangs, onExport, onImpersonate, onMessage, onResetPw, onRoleChange, onArchive, onDelete, onRestore, onPermDelete }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`user-actions-${u.user_id}`}><MoreHorizontal className="w-4 h-4" /></Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-48">
      <DropdownMenuItem onClick={onViewLangs} data-testid={`action-view-langs-${u.user_id}`}><Eye className="w-4 h-4 mr-2" /> View Languages</DropdownMenuItem>
      <DropdownMenuItem onClick={onExport} data-testid={`action-export-${u.user_id}`}><Download className="w-4 h-4 mr-2" /> Export Data</DropdownMenuItem>
      <DropdownMenuSeparator />
      {tab === 'active' && (
        <>
          <DropdownMenuItem onClick={onImpersonate} data-testid={`action-impersonate-${u.user_id}`}><UserCheck className="w-4 h-4 mr-2" /> View as User</DropdownMenuItem>
          <DropdownMenuItem onClick={onMessage} data-testid={`action-message-${u.user_id}`}><Mail className="w-4 h-4 mr-2" /> Send Email</DropdownMenuItem>
          <DropdownMenuItem onClick={onResetPw} data-testid={`action-reset-pw-${u.user_id}`}><KeyRound className="w-4 h-4 mr-2" /> Reset Password</DropdownMenuItem>
          {!isSelf && (
            <DropdownMenuItem onClick={onRoleChange} data-testid={`action-role-${u.user_id}`}>
              {u.is_admin ? <><ShieldOff className="w-4 h-4 mr-2" /> Demote to User</> : <><Shield className="w-4 h-4 mr-2" /> Promote to Admin</>}
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          {!isSelf && !u.is_admin && (
            <>
              <DropdownMenuItem onClick={onArchive} data-testid={`action-archive-${u.user_id}`}><Archive className="w-4 h-4 mr-2" /> Archive</DropdownMenuItem>
              <DropdownMenuItem onClick={onDelete} className="text-destructive focus:text-destructive" data-testid={`action-delete-${u.user_id}`}><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
            </>
          )}
        </>
      )}
      {tab === 'archived' && (
        <>
          <DropdownMenuItem onClick={onRestore} data-testid={`action-restore-${u.user_id}`}><RotateCcw className="w-4 h-4 mr-2" /> Restore</DropdownMenuItem>
          <DropdownMenuItem onClick={onDelete} className="text-destructive focus:text-destructive" data-testid={`action-delete-${u.user_id}`}><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
        </>
      )}
      {tab === 'deleted' && (
        <>
          <DropdownMenuItem onClick={onRestore} data-testid={`action-restore-${u.user_id}`}><RotateCcw className="w-4 h-4 mr-2" /> Restore</DropdownMenuItem>
          <DropdownMenuItem onClick={onPermDelete} className="text-destructive focus:text-destructive" data-testid={`action-perm-delete-${u.user_id}`}><Trash2 className="w-4 h-4 mr-2" /> Permanently Delete</DropdownMenuItem>
        </>
      )}
    </DropdownMenuContent>
  </DropdownMenu>
);

const LangDetail = ({ lang, onBack }) => (
  <Card className="bg-background-paper border-border">
    <CardHeader>
      <div className="flex items-center justify-between">
        <div>
          <CardTitle className="text-xl text-foreground">{lang.name}</CardTitle>
          {lang.native_name && <p className="text-secondary font-conlang">{lang.native_name}</p>}
        </div>
        <Button variant="ghost" onClick={onBack}>Back to list</Button>
      </div>
    </CardHeader>
    <CardContent className="space-y-6">
      <div className="grid md:grid-cols-3 gap-4">
        <div><p className="text-foreground-muted text-sm">Owner</p><p className="text-foreground">{lang.owner_name}</p><p className="text-foreground-subtle text-sm">{lang.owner_email}</p></div>
        <div><p className="text-foreground-muted text-sm">Visibility</p><p className="text-foreground flex items-center gap-2">{lang.visibility === 'public' ? <><Globe className="w-4 h-4 text-green-500" /> Public</> : <><Lock className="w-4 h-4" /> Private</>}</p></div>
        <div><p className="text-foreground-muted text-sm">Created</p><p className="text-foreground">{new Date(lang.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</p></div>
      </div>
      {lang.about && <div><p className="text-foreground-muted text-sm mb-1">About</p><p className="text-foreground">{lang.about}</p></div>}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[['Phonemes', lang.phonemes?.length || 0], ['Words', lang.words?.length || 0], ['Texts', lang.texts?.length || 0], ['Affixes', lang.affixes?.length || 0]].map(([l, v]) => (
          <div key={l} className="p-4 bg-accent rounded-lg"><p className="text-foreground-muted text-sm">{l}</p><p className="text-2xl font-bold text-foreground">{v}</p></div>
        ))}
      </div>
      {lang.words?.length > 0 && (
        <div>
          <h3 className="font-heading text-lg font-semibold text-foreground mb-3">Sample Words (first 10)</h3>
          <div className="grid md:grid-cols-2 gap-2">
            {lang.words.slice(0, 10).map(w => (
              <div key={w.word_id} className="p-2 bg-accent rounded flex justify-between">
                <span className="font-conlang text-foreground">{w.word}</span>
                <span className="text-foreground-muted">{w.definition}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </CardContent>
  </Card>
);

// ─── Bug Reports Panel ──────────────────────────────────────────────────────
const statusColors = {
  new: 'bg-blue-500/20 text-blue-400',
  reviewed: 'bg-yellow-500/20 text-yellow-400',
  resolved: 'bg-emerald-500/20 text-emerald-400',
  dismissed: 'bg-zinc-500/20 text-zinc-400',
};

const downloadJson = (data, filename) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
};

const BugReportsPanel = () => {
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('new');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  const [checked, setChecked] = useState(new Set());
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = React.useRef(null);

  const fetchReports = useCallback(async (status) => {
    setLoading(true);
    try {
      const params = status && status !== 'all' ? `?status=${status}&limit=100` : '?limit=100';
      const r = await axios.get(`${API}/bug-reports${params}`, { withCredentials: true });
      setReports(r.data.reports); setTotal(r.data.total);
    } catch { toast.error('Failed to load reports'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchReports(statusFilter); setChecked(new Set()); }, [statusFilter, fetchReports]);

  const openDetail = async (reportId) => {
    setDetailLoading(true); setSelected(reportId);
    try {
      const r = await axios.get(`${API}/bug-reports/${reportId}`, { withCredentials: true });
      setDetail(r.data); setAdminNotes(r.data.admin_notes || '');
    } catch { toast.error('Failed to load report'); }
    setDetailLoading(false);
  };

  const updateStatus = async (reportId, status) => {
    setUpdating(true);
    try {
      await axios.put(`${API}/bug-reports/${reportId}/status`, { status, admin_notes: adminNotes }, { withCredentials: true });
      toast.success(`Report marked as ${status}`);
      fetchReports(statusFilter);
      if (detail?.report_id === reportId) setDetail({ ...detail, status, admin_notes: adminNotes });
    } catch { toast.error('Failed to update'); }
    setUpdating(false);
  };

  const deleteReport = async (reportId) => {
    try {
      await axios.delete(`${API}/bug-reports/${reportId}`, { withCredentials: true });
      toast.success('Report deleted'); setSelected(null); setDetail(null);
      setChecked((prev) => { const n = new Set(prev); n.delete(reportId); return n; });
      fetchReports(statusFilter);
    } catch { toast.error('Failed to delete'); }
  };

  // ── Selection helpers ──
  const toggleCheck = (id, e) => {
    e.stopPropagation();
    setChecked((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleAll = () => {
    if (checked.size === reports.length) setChecked(new Set());
    else setChecked(new Set(reports.map((r) => r.report_id)));
  };

  // ── Export ──
  const handleExport = async (ids) => {
    if (!ids.length) return;
    setExporting(true);
    try {
      const r = await axios.post(`${API}/bug-reports/export`, { report_ids: ids }, { withCredentials: true });
      const ts = new Date().toISOString().slice(0, 10);
      const filename = ids.length === 1
        ? `bug-report-${ids[0]}-${ts}.json`
        : `bug-reports-${ids.length}-${ts}.json`;
      downloadJson(r.data, filename);
      toast.success(`Exported ${r.data.export_info.count} report(s)`);
    } catch { toast.error('Export failed'); }
    setExporting(false);
  };

  const handleExportSingle = async (reportId) => {
    await handleExport([reportId]);
  };

  // ── Import ──
  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const reportsToImport = data.reports || (data.report_id ? [data] : []);
      if (!reportsToImport.length) { toast.error('No valid reports found in file'); setImporting(false); return; }
      const r = await axios.post(`${API}/bug-reports/import`, { reports: reportsToImport }, { withCredentials: true });
      toast.success(r.data.message);
      fetchReports(statusFilter);
    } catch (err) {
      if (err instanceof SyntaxError) toast.error('Invalid JSON file');
      else toast.error(err.response?.data?.detail || 'Import failed');
    }
    setImporting(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-heading text-2xl font-bold text-foreground">Bug Reports ({total})</h2>
        <div className="flex items-center gap-2">
          <input type="file" ref={fileInputRef} accept=".json" className="hidden" onChange={handleImportFile} data-testid="bug-import-input" />
          <Button
            variant="outline" size="sm" className="border-border"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            data-testid="bug-import-btn"
          >
            {importing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
            Import
          </Button>
          {checked.size > 0 && (
            <Button
              variant="outline" size="sm" className="border-border"
              onClick={() => handleExport([...checked])}
              disabled={exporting}
              data-testid="bug-export-selected-btn"
            >
              {exporting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
              Export {checked.size} selected
            </Button>
          )}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 bg-background-paper border-border" data-testid="bug-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="reviewed">Reviewed</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="dismissed">Dismissed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
      ) : reports.length === 0 ? (
        <Card className="bg-background-paper border-border"><CardContent className="py-12 text-center text-foreground-muted">No bug reports found.</CardContent></Card>
      ) : (
        <>
          {/* Select all row */}
          <div className="flex items-center gap-3 px-1">
            <input
              type="checkbox"
              checked={checked.size === reports.length && reports.length > 0}
              onChange={toggleAll}
              className="w-4 h-4 rounded border-border accent-primary cursor-pointer"
              data-testid="bug-select-all"
            />
            <span className="text-xs text-foreground-muted">
              {checked.size > 0 ? `${checked.size} of ${reports.length} selected` : 'Select all'}
            </span>
          </div>
          <div className="grid gap-3">
            {reports.map((r) => (
              <Card
                key={r.report_id}
                className={`bg-background-paper border-border cursor-pointer hover:border-primary/40 transition-colors ${selected === r.report_id ? 'border-primary/60' : ''} ${checked.has(r.report_id) ? 'ring-1 ring-primary/30' : ''}`}
                onClick={() => openDetail(r.report_id)}
                data-testid={`bug-report-${r.report_id}`}
              >
                <CardContent className="py-4 px-5 flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={checked.has(r.report_id)}
                    onChange={(e) => toggleCheck(r.report_id, e)}
                    className="w-4 h-4 mt-1 rounded border-border accent-primary cursor-pointer flex-shrink-0"
                    data-testid={`bug-check-${r.report_id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[r.status]}`}>{r.status}</span>
                      <span className="text-xs text-foreground-subtle">{fmt(r.created_at)}</span>
                      {r.has_screenshot && <Camera className="w-3.5 h-3.5 text-foreground-subtle" />}
                      {r.imported && <span className="text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary/60">imported</span>}
                    </div>
                    <p className="text-foreground text-sm truncate">{r.description}</p>
                    <p className="text-xs text-foreground-subtle mt-1">{r.user_name} ({r.user_email}) &middot; {r.page_url}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={() => { setSelected(null); setDetail(null); }}>
        <DialogContent className="bg-background-paper border-border max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="bug-report-detail">
          {detailLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
          ) : detail ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-foreground flex items-center gap-2">
                  Bug Report
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[detail.status]}`}>{detail.status}</span>
                </DialogTitle>
                <DialogDescription className="text-foreground-muted">
                  {detail.user_name} ({detail.user_email}) &middot; {fmt(detail.created_at)}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div><Label className="text-foreground-muted text-xs">Description</Label><p className="text-foreground text-sm mt-1 whitespace-pre-wrap">{detail.description}</p></div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><Label className="text-foreground-muted text-xs">Page URL</Label><p className="text-foreground break-all mt-1">{detail.page_url}</p></div>
                  <div><Label className="text-foreground-muted text-xs">Screen Size</Label><p className="text-foreground mt-1">{detail.screen_size || '—'}</p></div>
                </div>
                <div><Label className="text-foreground-muted text-xs">Browser</Label><p className="text-foreground text-xs mt-1 break-all">{detail.browser_info || '—'}</p></div>
                {detail.console_errors?.length > 0 && (
                  <div>
                    <Label className="text-foreground-muted text-xs">Console Errors ({detail.console_errors.length})</Label>
                    <div className="mt-1 bg-background rounded-md border border-border p-3 max-h-40 overflow-y-auto font-mono text-xs text-foreground-muted space-y-1">
                      {detail.console_errors.map((e, i) => <div key={i}>{e}</div>)}
                    </div>
                  </div>
                )}
                {detail.screenshot && (
                  <div>
                    <Label className="text-foreground-muted text-xs">Screenshot</Label>
                    <img src={detail.screenshot} alt="Bug screenshot" className="mt-1 rounded-md border border-border w-full" data-testid="bug-detail-screenshot" />
                  </div>
                )}
                <div>
                  <Label className="text-foreground-muted text-xs">Admin Notes</Label>
                  <Textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    className="mt-1 bg-background border-border min-h-[60px]"
                    placeholder="Add notes about this report..."
                    data-testid="bug-admin-notes"
                  />
                </div>
              </div>
              <DialogFooter className="flex-wrap gap-2">
                <Button variant="destructive" size="sm" onClick={() => deleteReport(detail.report_id)} data-testid="bug-delete-btn">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                </Button>
                <Button variant="outline" size="sm" className="border-border" onClick={() => handleExportSingle(detail.report_id)} disabled={exporting} data-testid="bug-export-single-btn">
                  <Download className="w-3.5 h-3.5 mr-1" /> Download JSON
                </Button>
                <div className="flex gap-2 ml-auto">
                  {detail.status !== 'reviewed' && (
                    <Button variant="outline" size="sm" className="border-border" onClick={() => updateStatus(detail.report_id, 'reviewed')} disabled={updating}>Reviewed</Button>
                  )}
                  {detail.status !== 'resolved' && (
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => updateStatus(detail.report_id, 'resolved')} disabled={updating}>Resolved</Button>
                  )}
                  {detail.status !== 'dismissed' && (
                    <Button variant="outline" size="sm" className="border-border text-foreground-muted" onClick={() => updateStatus(detail.report_id, 'dismissed')} disabled={updating}>Dismiss</Button>
                  )}
                </div>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
